import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useUserResetMfaMutation } from "@/v2-backoffice-client/user/resetMfa/user.resetMfa.mutation"
import { USER_RESET_MFA_DONE_TITLE } from "@/v2-backoffice-client/user/resetMfa/user.resetMfa.constants"

export type UserResetMfaMenuItemProps = Omit<ActionMenuItemProps, "onClick"> & {
  userId: number
  confirmDescription?: string
  onCompleted?(): void
}

// Clears the user's registered MFA phone number(s) in Azure B2C. The fix for
// the wrong-phone-number lockout: once MFA is enforced, a user who registered
// an unreachable number can never receive the code; resetting lets them
// re-enrol a correct one on the next sign-in.
function UserResetMfaMenuItem(props: UserResetMfaMenuItemProps) {
  const { userId, confirmDescription, onCompleted, ...menuItemProps } = props

  const [resetMfa, { loading, error }] = useUserResetMfaMutation({
    // The menu closes on confirm and nothing on screen changes afterwards,
    // so the operator needs to be told it happened (the button twin already
    // does this).
    onCompleted() {
      toast({ variant: "success", title: USER_RESET_MFA_DONE_TITLE })
      onCompleted?.()
    },
  })

  return (
    <ActionMenu.ConfirmItem
      variant="warning"
      iconSrc="shield-check"
      isLoading={loading}
      error={error}
      errorTitle="Failed to reset MFA"
      confirmTitle="Reset MFA"
      confirmDescription={
        confirmDescription ??
        "This clears the user's registered MFA phone number(s) in Azure B2C, so they set up MFA again on their next sign-in. Use it when a user is locked out because they registered a wrong phone number. Existing sessions are unaffected."
      }
      confirmText="Reset MFA"
      confirmVariant="warning"
      onConfirm={async () => {
        await resetMfa({ variables: { id: userId } })
      }}
      {...menuItemProps}
    >
      Reset MFA
    </ActionMenu.ConfirmItem>
  )
}

export default UserResetMfaMenuItem
